import { Activity, Clock, CheckCircle, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useQueueDashboard } from '@/hooks/useQueue'
import { formatDuration } from '@/lib/utils'

export function QueueStatus() {
  const { data, isLoading, error } = useQueueDashboard()

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Fila de Processamento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Fila de Processamento</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-muted-foreground">
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm">Serviço de processamento indisponível</span>
            </div>
            <p className="text-xs text-muted-foreground">
              O servidor whisper-processor não está acessível.
              Acesse o dashboard diretamente em{' '}
              <a
                href="http://vcall.grupovoalle.com.br:5001/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                vcall:5001/dashboard
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  const summary = data?.summary || {
    active_count: 0,
    processing_count: 0,
    waiting_count: 0,
    recent_count: 0,
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          Fila de Processamento
          <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-500/10">
            <Activity className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{summary.active_count}</p>
              <p className="text-xs text-muted-foreground">Conferências Ativas</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-500/10">
            <Clock className="h-8 w-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold">{summary.processing_count}</p>
              <p className="text-xs text-muted-foreground">Processando</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-500/10">
            <Clock className="h-8 w-8 text-orange-500" />
            <div>
              <p className="text-2xl font-bold">{summary.waiting_count}</p>
              <p className="text-xs text-muted-foreground">Na Fila</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-green-500/10">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{summary.recent_count}</p>
              <p className="text-xs text-muted-foreground">Recentes</p>
            </div>
          </div>
        </div>

        {/* Processing Sessions */}
        {data?.processing_sessions && data.processing_sessions.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Em Processamento</h4>
            {data.processing_sessions.slice(0, 3).map((session, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded-md bg-muted/50">
                <span className="text-sm font-medium truncate max-w-[150px]">
                  {session.room_name || 'Reunião'}
                </span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${session.progress || 0}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground w-12 text-right">
                    {session.progress ? `${Math.round(session.progress)}%` : '...'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ETA Info */}
        {data?.eta_info && data.eta_info.total_waiting > 0 && (
          <div className="mt-4 p-3 rounded-lg bg-muted/50">
            <p className="text-sm">
              <span className="font-medium">{data.eta_info.total_waiting}</span> reuniões na fila
              {data.eta_info.estimated_minutes > 0 && (
                <>
                  {' '}• ETA: <span className="font-medium">{formatDuration(data.eta_info.estimated_minutes * 60)}</span>
                </>
              )}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
